import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { StaticBatch } from '../engine/Batch';
import { hash } from '../engine/noise';
import { ext, octShape, ring, star4 } from '../engine/shapes';
import type { Textures } from '../engine/textures';
import { Glows } from '../fx/Glows';
import { fxUniforms } from '../fx/common';
import { addFakeLights } from '../fx/fakeLights';
import { BOARD_PARTNER, L } from '../game/config';
import type { Materials } from './materials';
import { buildSpawnFog } from './SpawnFog';
import { noiseTexture } from '../fx/noise';

export interface Cell {
  r: number; c: number;
  x: number; z: number;
  w: number; d: number;
  /** Top face of the altar slab (rim of the well). */
  topY: number;
  /** Floor of the well. */
  floorY: number;
  code: string;
  index: number;
}

export interface Altar {
  cells: Cell[];
  at(r: number, c: number): Cell | undefined;
  rows: number; cols: number;
  /** Per-cell highlight frames (additive, instanced) — set colour to black to hide. */
  frames: THREE.InstancedMesh;
  /** Lit grooves between neighbours (buff links), instanced; index via linkIndex. */
  links: { a: number; b: number; mesh: THREE.InstancedMesh; i: number; x: number; z: number; y: number; vertical: boolean; len: number }[];
}

export interface WorldCtx {
  scene: THREE.Scene;
  batch: StaticBatch;
  M: Materials;
  tex: Textures;
  glows: Glows;
  floorGlows: Glows;
  /** Flickering lights (fires); animated by the game. */
  fires: { l: THREE.PointLight; base: number; ph: number }[];
  /** Ambient dust motes near the gate (quality tier). */
  dust?: number;
  /** Cheaper spawn fog for fill-rate-bound GPUs. */
  fogLite?: boolean;
}

const tmpM = new THREE.Matrix4();

export function blob(ctx: WorldCtx, x: number, y: number, z: number, w: number, d: number, op = 0.7) {
  // Opacity is baked by scaling darkness through vertex count? Keep it simple: one material, alpha via size overlap.
  const g = new THREE.PlaneGeometry(w, d); g.rotateX(-Math.PI / 2);
  const mat = op >= 0.8 ? ctx.M.blob : blobMat(ctx, op);
  ctx.batch.add(g, mat, x, y, z, { cast: false });
}
const blobMats = new Map<number, THREE.MeshBasicMaterial>();
function blobMat(ctx: WorldCtx, op: number) {
  const k = Math.round(op * 10) / 10;
  let m = blobMats.get(k);
  if (!m) { m = ctx.M.blob.clone(); m.opacity = k; blobMats.set(k, m); }
  return m;
}

export function buildAltar(ctx: WorldCtx, cx: number, cz: number, grid: string[][], cw: number, cd: number, gx: number, gz: number, o: {
  pad?: number; y0?: number; plinth?: number; bh?: number; th?: number; mat?: THREE.Material; inlayMat?: THREE.Material; grooves?: boolean; interactive?: boolean;
} = {}): Altar {
  const { batch, M } = ctx;
  const rows = grid.length, cols = grid[0].length, pad = o.pad ?? 0.34, y0 = o.y0 ?? 0;
  const Wd = cols * cw + (cols - 1) * gx + 2 * pad, Dd = rows * cd + (rows - 1) * gz + 2 * pad;
  const inlay = o.inlayMat ?? M.bronze;
  if (o.plinth) batch.add(ext(octShape(Wd + 0.5, Dd + 0.5, 0.7), o.plinth, 0.04), M.wall, cx, 0, cz);
  const bh = o.bh ?? 0.26, th = o.th ?? 0.2, mat = o.mat ?? M.basalt;
  batch.add(ext(octShape(Wd + 0.22, Dd + 0.22, 0.55), bh, 0.04), M.base, cx, y0, cz);
  blob(ctx, cx, y0 + 0.006, cz, Wd + 1.6, Dd + 1.6, 0.85);
  batch.add(ext(ring(octShape(Wd + 0.3, Dd + 0.3, 0.58), octShape(Wd + 0.16, Dd + 0.16, 0.52)), 0.05), inlay, cx, y0 + bh * 0.45, cz, { cast: false });
  const baseTop = y0 + bh + 0.08, cells: Cell[] = [];
  const top = octShape(Wd, Dd, 0.48), cc = Math.min(cw, cd) * 0.28;
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    const dx = (c - (cols - 1) / 2) * (cw + gx), dz = (r - (rows - 1) / 2) * (cd + gz);
    top.holes.push(new THREE.Path(octShape(cw, cd, cc, dx, -dz).getPoints()));
    cells.push({ r, c, x: cx + dx, z: cz + dz, w: cw, d: cd, code: grid[r][c], topY: 0, floorY: 0, index: cells.length });
  }
  batch.add(ext(top, th, 0.03), mat, cx, baseTop, cz);
  const topY = baseTop + th + 0.06;
  {
    const pts = octShape(Wd - 0.16, Dd - 0.16, 0.4).getSpacedPoints(Math.round((Wd + Dd) * 2.2)); pts.pop();
    const stud = new THREE.SphereGeometry(0.035, 10, 6);
    pts.forEach((p) => batch.add(stud, inlay, cx + p.x, topY - 0.02, cz - p.y, { scale: [1, 0.45, 1], cast: false }));
  }
  const wellGeo = new THREE.ShapeGeometry(octShape(cw, cd, cc)); wellGeo.rotateX(-Math.PI / 2);
  const ringGeo = ext(ring(octShape(cw + 0.2, cd + 0.2, cc + 0.08), octShape(cw + 0.07, cd + 0.07, cc + 0.03)), 0.012);
  const frameGeo = ext(ring(octShape(cw + 0.3, cd + 0.3, cc + 0.12), octShape(cw + 0.04, cd + 0.04, cc + 0.02)), 0.004);
  const frames = new THREE.InstancedMesh(frameGeo, new THREE.MeshBasicMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }), cells.length);
  frames.frustumCulled = false; frames.renderOrder = 3;
  cells.forEach((C, i) => {
    C.topY = topY; C.floorY = baseTop + 0.004;
    batch.add(wellGeo, M.well, C.x, C.floorY, C.z, { cast: false });
    batch.add(ringGeo, inlay, C.x, topY - 0.008, C.z, { cast: false });
    frames.setMatrixAt(i, tmpM.makeTranslation(C.x, topY + 0.006, C.z));
    frames.setColorAt(i, new THREE.Color(0));
  });
  if (o.interactive) ctx.scene.add(frames);
  const at = (r: number, c: number) => (r >= 0 && c >= 0 && r < rows && c < cols ? cells[r * cols + c] : undefined);

  // Grooves between neighbours: dark channel always; lit overlay instanced for buff links.
  const links: Altar['links'] = [];
  const pairs: [Cell, Cell, boolean][] = [];
  for (const A of cells) for (const [dr, dc] of [[0, 1], [1, 0]] as const) { const B = at(A.r + dr, A.c + dc); if (B) pairs.push([A, B, dr === 1]); }
  const litGeoH = new THREE.BoxGeometry(1, 0.02, 0.06), litGeoV = new THREE.BoxGeometry(0.06, 0.02, 1);
  // a lit groove is molten amber: dim at the channel walls, with energy pulses running from the sun
  // relic toward its neighbour (the instance's length scale is signed to point the flow)
  const litMat = (axis: 'x' | 'z') => new THREE.ShaderMaterial({
    uniforms: { uTime: fxUniforms.uTime }, side: THREE.DoubleSide,
    vertexShader: `varying float vAlong; varying float vAcross;
      void main() {
        vAlong = position.${axis} + 0.5; vAcross = position.${axis === 'x' ? 'z' : 'x'} / 0.03;
        gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `uniform float uTime; varying float vAlong; varying float vAcross;
      void main() {
        float core = 1.0 - vAcross * vAcross;
        float pulse = pow(max(0.0, sin((vAlong * 2.0 - uTime * 1.6) * 6.2832)), 6.0);
        float ends = smoothstep(0.0, 0.12, vAlong) * smoothstep(1.0, 0.88, vAlong);
        vec3 col = vec3(0.55, 0.26, 0.06) + vec3(0.75, 0.48, 0.16) * core + vec3(0.9, 0.62, 0.25) * pulse * core;
        gl_FragColor = vec4(col * (0.35 + 0.65 * ends), 1.0);
      }`,
  });
  const litH = new THREE.InstancedMesh(litGeoH, litMat('x'), Math.max(1, pairs.filter((p) => !p[2]).length));
  const litV = new THREE.InstancedMesh(litGeoV, litMat('z'), Math.max(1, pairs.filter((p) => p[2]).length));
  let ih = 0, iv = 0;
  for (const [A, B, vertical] of pairs) {
    const len = vertical ? gz + 0.1 : gx + 0.1, mx = (A.x + B.x) / 2, mz = (A.z + B.z) / 2;
    if (o.grooves !== false) batch.add(vertical ? new THREE.BoxGeometry(0.06, 0.02, len) : new THREE.BoxGeometry(len, 0.02, 0.06), M.groove, mx, topY - 0.006, mz, { cast: false });
    const mesh = vertical ? litV : litH, i = vertical ? iv++ : ih++;
    mesh.setMatrixAt(i, tmpM.makeScale(0, 0, 0));
    links.push({ a: A.index, b: B.index, mesh, i, x: mx, z: mz, y: topY - 0.002, vertical, len });
  }
  if (o.interactive) { [litH, litV].forEach((m) => { m.frustumCulled = false; ctx.scene.add(m); }); }
  return { cells, at, rows, cols, frames, links };
}

/** Flat environment fill for the Lambert floor (stands in for image-based diffuse light). */
export const floorEnvFill = { value: new THREE.Color(1.15, 1.25, 1.45) };

/** Gate wound: x = accumulated breach damage 0..1 (heals slowly), y = time of the last breach. */
export const gateWound = { value: new THREE.Vector2(0, -100) };

/** 0..1 while the overload skill charges the player's altar (eased by Combat). */
export const runeOverload = { value: 0 };

/** Time and strength of the last altar flash (merges, summons) for the floor rune circle. */
export const runeFlash = { value: new THREE.Vector2(-100, 0) };

export interface WorldInfo {
  myAltar: Altar;
  partnerAltar: Altar | null;
  sealPos: THREE.Vector3;
  smoke: { t: THREE.Texture; sp: number }[];
  dust: THREE.Points;
  key: THREE.DirectionalLight;
  spArc: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; glows: number[] };
  sealCore: THREE.Mesh;
  sealStar: THREE.Mesh;
  /** One dedicated pedestal to the left of each player's board. */
  totemSockets: { owner: 'player' | 'ally'; x: number; z: number; y: number }[];
  /** Open flames that shed embers into the courtyard (ambient life, no lights). */
  flames: { pos: THREE.Vector3; color: number; rate: number; radius: number; fire?: number; fireSize?: number }[];
}

export function buildWorld(ctx: WorldCtx, coop: boolean, myGrid: string[][]): WorldInfo {
  const { scene, batch, M, tex, glows } = ctx;
  batch.useWorldUV(M.basalt, 0.42); batch.useWorldUV(M.base, 0.42); batch.useWorldUV(M.cold, 0.42); batch.useWorldUV(M.wall, 0.62);
  const CZ = L.roadZ, RD = L.roadDepth, RL = L.roadLength, gateX = L.gateX, gateW = L.gateW, wallZ = L.wallZ, wallH = L.wallH;

  /* lights — a fixed, small set so shader permutations never change at runtime */
  // cold night above, warm embers below: a cool moonlit sky fill lifts the far corners out of pure black,
  // so the fires read warm against it
  scene.add(new THREE.HemisphereLight(0x34405a, 0x140b06, 0.6));
  const key = new THREE.DirectionalLight(0xb4c6e6, 2.7); key.position.set(-6, 13, 7);
  key.castShadow = true;
  const rim = new THREE.DirectionalLight(0x7f9fd8, 2.5); rim.position.set(-3, 6, -10);
  const fill = new THREE.DirectionalLight(0xffb27a, 0.35); fill.position.set(6, 4, 8);
  scene.add(key, rim, fill);
  // Fire light is shader-only (fx/fakeLights.ts): the light object only carries its parameters and is never
  // added to the scene, so no material pays for it except those that visibly catch it.
  addFakeLights(M.wall); addFakeLights(M.base); addFakeLights(M.basalt);
  const fire = (color: number, intensity: number, dist: number, x: number, y: number, z: number, ph: number) => {
    const l = new THREE.PointLight(color, intensity, dist, 2); l.position.set(x, y, z);
    ctx.fires.push({ l, base: intensity, ph });
    return l;
  };

  /* ground (wide enough for landscape screens) */
  {
    // Lambert, not PBR: the floor is near-matte (roughness 0.9) and covers most of the screen, so the GGX
    // specular it used to pay for every pixel was almost invisible
    const fm = new THREE.MeshLambertMaterial({ name: 'floor', map: tex.floor.map, bumpMap: tex.floor.bump, bumpScale: 3, emissive: 0xffffff, emissiveIntensity: 0.2, color: 0x7a736d });
    [tex.floor.map, tex.floor.bump].forEach((t) => { t.wrapS = t.wrapT = THREE.MirroredRepeatWrapping; t.repeat.set(6, 4); t.offset.set(-0.5, 0.5); });
    // the lava seams burn where the fight is — hottest at the gate and along the road — and die down
    // toward the courtyard's edges, breathing slowly, so the eye is drawn to the action
    fm.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = fxUniforms.uTime; sh.uniforms.uEnvFill = floorEnvFill; sh.uniforms.uNoise = { value: noiseTexture() };
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec2 vFloorW;')
        .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvFloorW = (modelMatrix * vec4(transformed, 1.0)).xz;');
      sh.fragmentShader = cheapShadows(sh.fragmentShader)
        .replace('#include <common>', `#include <common>
          uniform float uTime; uniform vec3 uEnvFill; uniform sampler2D uNoise; varying vec2 vFloorW;`)
        .replace('#include <color_fragment>', `#include <color_fragment>
          {
            // shadows of high clouds drift slowly across the moonlit courtyard
            // one read of the shared tiling noise (two independent fields in RG) instead of hashing 8 corners
            vec2 cp = vFloorW * 0.09 + vec2(uTime * 0.018, uTime * 0.007);
            vec2 n = texture2D(uNoise, cp * 0.125).rg;
            float cloud = n.x * 0.65 + n.y * 0.35;
            diffuseColor.rgb *= mix(1.0, 0.68, smoothstep(0.45, 0.75, cloud));
          }`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          {
            // the seam mask lives in the colour map's alpha; its hue (255, 80, 14) is fixed
            totalEmissiveRadiance *= sRGBTransferEOTF(vec4(vec3(1.0, 80.0 / 255.0, 14.0 / 255.0) * sampledDiffuseColor.a, 1.0)).rgb;
            vec2 g = vFloorW - vec2(${L.gateX.toFixed(2)}, ${L.gateZ.toFixed(2)});
            float road = exp(-pow((vFloorW.y - ${L.roadZ.toFixed(2)}) / 2.2, 2.0)) * smoothstep(${(L.fogStartX - 1).toFixed(2)}, ${L.gateX.toFixed(2)}, vFloorW.x);
            float heat = 0.22 + 2.2 * exp(-dot(g, g) / 7.0) + 0.7 * road;
            heat *= 0.82 + 0.18 * sin(uTime * 0.7 + vFloorW.x * 0.6 + vFloorW.y * 0.4);
            totalEmissiveRadiance *= heat;
            // Lambert has no image-based light: stand in for the environment's diffuse light with a flat,
            // cool fill (tuned so the floor matches the PBR version's brightness)
            totalEmissiveRadiance += diffuseColor.rgb * uEnvFill;
          }`);
    };
    fm.customProgramCacheKey = () => 'floor-heat-clouds-lambert-cheapshadow-seamalpha-noisetex';
    addFakeLights(fm);
    const g = new THREE.PlaneGeometry(96, 128); g.rotateX(-Math.PI / 2);
    const ground = new THREE.Mesh(g, fm); ground.position.z = -1; ground.receiveShadow = true; ground.matrixAutoUpdate = false; ground.updateMatrix();
    // Drawn last among opaques (after the road, ro 1): everything standing on it is already in the depth buffer,
    // so the lit floor shader runs only where the floor is actually seen (−15% lit fragments, measured).
    ground.renderOrder = 2;
    scene.add(ground);
  }

  /* road */
  {
    const rm = new THREE.MeshStandardMaterial({ name: 'road', map: tex.road.map, bumpMap: tex.road.bump, bumpScale: 2.5, emissiveMap: tex.road.emissive, emissive: 0xffffff, emissiveIntensity: 0.55, roughness: 0.72, color: 0x9a938c });
    const tailBase = rm.clone();
    // The road reads as a route: a trodden, smoother lane down the middle, grime toward the curbs,
    // and its seams warming toward the gate where the march ends.
    rm.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = fxUniforms.uTime;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec2 vRoadW;')
        .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvRoadW = (modelMatrix * vec4(transformed, 1.0)).xz;');
      sh.fragmentShader = cheapShadows(sh.fragmentShader)
        .replace('#include <common>', '#include <common>\nuniform float uTime; varying vec2 vRoadW;')
        .replace('#include <color_fragment>', `#include <color_fragment>
          float across = abs(vRoadW.y - ${CZ.toFixed(2)}) / ${(RD / 2).toFixed(2)};
          float lane = 1.0 - smoothstep(0.25, 0.7, across);
          diffuseColor.rgb *= mix(0.62, 1.0, 1.0 - smoothstep(0.7, 1.0, across)) * (1.0 + lane * 0.14);`)
        .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\n roughnessFactor *= 1.0 - lane * 0.22;')
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          float toGate = smoothstep(${(L.fogEndX).toFixed(2)}, ${L.gateX.toFixed(2)}, vRoadW.x);
          totalEmissiveRadiance *= mix(0.55, 1.9, toGate * toGate) * (0.85 + 0.15 * sin(uTime * 0.9 - vRoadW.x * 0.8));`);
    };
    rm.customProgramCacheKey = () => 'road-route-cheapshadow';
    addFakeLights(rm);
    const g = new THREE.PlaneGeometry(RL, RD); g.rotateX(-Math.PI / 2);
    const road = new THREE.Mesh(g, rm); road.position.set(L.roadCenterX, 0.012, CZ); road.receiveShadow = true; road.matrixAutoUpdate = false; road.updateMatrix(); road.renderOrder = 1; scene.add(road);
    // extend the road into the fog on the spawn side
    const fogRoadEnd = -48, fogRoadStart = L.spawnX - 0.8;
    const g2 = new THREE.PlaneGeometry(fogRoadStart - fogRoadEnd, RD); g2.rotateX(-Math.PI / 2);
    const tailMat = tailBase;
    for (const key of ['map', 'bumpMap', 'emissiveMap'] as const) {
      const t = tailMat[key]!.clone(); t.wrapS = THREE.RepeatWrapping;
      t.repeat.x = (fogRoadStart - fogRoadEnd) / RL; t.needsUpdate = true;
      tailMat[key] = t;
    }
    const tail = new THREE.Mesh(g2, tailMat); tail.position.set((fogRoadStart + fogRoadEnd) / 2, 0.011, CZ); tail.receiveShadow = true; tail.matrixAutoUpdate = false; tail.updateMatrix(); tail.renderOrder = 1; scene.add(tail);
  }

  /* Straight side wall from the diagonal gate, continuing out of the frame. */
  const wallRun = (x0: number, z0: number, x1: number, z1: number, buttresses = false) => {
    const length = Math.hypot(x1 - x0, z1 - z0), angle = -Math.atan2(z1 - z0, x1 - x0);
    const x = (x0 + x1) / 2, z = (z0 + z1) / 2, rot: [number, number, number] = [0, angle, 0];
    batch.add(new THREE.BoxGeometry(length, wallH, 0.5), M.wall, x, wallH / 2, z, { rot });
    batch.add(new THREE.BoxGeometry(length + 0.02, 0.1, 0.66), M.wall, x, wallH + 0.05, z, { rot });
    if (buttresses) for (let d = 0.35; d < length; d += 1.4) {
      const u = d / length, px = x0 + (x1 - x0) * u, pz = z0 + (z1 - z0) * u;
      batch.add(new THREE.BoxGeometry(0.32, wallH + 0.25, 0.7), M.wall, px, (wallH + 0.25) / 2, pz, { rot });
      batch.add(new THREE.BoxGeometry(0.42, 0.12, 0.78), M.wall, px, wallH + 0.31, pz, { rot });
    }
  };
  wallRun(L.cornerX, wallZ, L.cornerX, L.cornerZ, true);
  const gc = Math.cos(L.gateAngle), gs = Math.sin(L.gateAngle);
  const gatePoint = (u: number, v = 0) => ({ x: gateX + gc * u + gs * v, z: L.gateZ - gs * u + gc * v });
  const gatePart = (geo: THREE.BufferGeometry, mat: THREE.Material, u: number, y: number, v = 0, rx = 0, scale?: number | [number, number, number]) => {
    const p = gatePoint(u, v);
    batch.add(geo, mat, p.x, y, p.z, { rot: [rx, L.gateAngle, 0], scale });
  };
  for (const [u0, u1] of [[-L.gateWallHalf, -gateW / 2 - 0.18], [gateW / 2 + 0.18, L.gateWallHalf]]) {
    const a = gatePoint(u0), b = gatePoint(u1); wallRun(a.x, a.z, b.x, b.z);
  }
  [-gateW / 2 - 0.15, gateW / 2 + 0.15].forEach((u) => {
    gatePart(new THREE.BoxGeometry(0.32, 2.1, 0.72), M.wall, u, 1.05);
    gatePart(new THREE.BoxGeometry(0.44, 0.14, 0.84), M.wall, u, 2.15);
    gatePart(new THREE.ConeGeometry(0.2, 0.4, 4), M.iron, u, 2.42);
  });
  {
    const archH = gateW / 2 + 0.22;
    const s = new THREE.Shape(); s.moveTo(-gateW / 2 - 0.02, 0); s.lineTo(gateW / 2 + 0.02, 0); s.lineTo(gateW / 2 + 0.02, archH); s.lineTo(-gateW / 2 - 0.02, archH); s.closePath();
    const h = new THREE.Path(); h.moveTo(-gateW / 2, 0); h.lineTo(gateW / 2, 0); h.absarc(0, 0, gateW / 2, 0, Math.PI, false); s.holes.push(h);
    gatePart(new THREE.ExtrudeGeometry(s, { depth: 0.6, bevelEnabled: false, curveSegments: 20 }), M.wall, 0, 1.15, -0.3);
  }
  {
    // the portal is living fire: noise flames rising and tearing into tongues, white-hot heart, soot at the rim
    const hp = new THREE.Mesh(new THREE.PlaneGeometry(gateW, 1.62), new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uWound: gateWound },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform vec2 uWound; varying vec2 vUv;
        float h2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h2(i), h2(i + vec2(1, 0)), f.x), mix(h2(i + vec2(0, 1)), h2(i + vec2(1, 1)), f.x), f.y); }
        float fbm(vec2 p) { return vn(p) * 0.55 + vn(p * 2.1 + 3.1) * 0.3 + vn(p * 4.3 + 7.7) * 0.15; }
        void main() {
          vec2 p = vUv;
          // breaches enrage the fire: a surge on each one, and a faster, redder churn while the wound is fresh
          float surge = exp(-max(0.0, uTime - uWound.y) * 2.5), rage = uWound.x;
          vec2 q = vec2(p.x * 2.2, p.y * 1.6 - uTime * (0.75 + rage * 0.6 + surge * 0.8));
          float warp = fbm(q * 1.4 + vec2(0.0, uTime * 0.2));
          float n = fbm(q * 2.4 + vec2(warp * 1.3, 0.0));
          float flame = clamp(n * 1.75 - p.y * (0.95 - rage * 0.25 - surge * 0.35) - 0.05 + surge * 0.2, 0.0, 1.0);
          vec3 col = mix(vec3(0.22, 0.02, 0.005), vec3(1.15, 0.26, 0.04), smoothstep(0.12, 0.55, flame));
          col = mix(col, vec3(2.1, 1.25, 0.45), smoothstep(0.72, 1.0, flame));
          float heart = exp(-pow((p.x - 0.5) * 3.0, 2.0)) * (1.0 - p.y * 0.7);
          col += vec3(0.9, 0.4, 0.1) * heart * (0.35 + 0.15 * sin(uTime * 3.1));
          float rim = smoothstep(0.0, 0.14, p.x) * smoothstep(1.0, 0.86, p.x) * smoothstep(1.0, 0.82, p.y);
          col *= mix(vec3(1.0), vec3(1.25, 0.7, 0.55), rage);
          gl_FragColor = vec4(col * (0.3 + 0.7 * rim) * (1.0 + surge * 0.9), 1.0);
        }`,
    }));
    const p = gatePoint(0, -0.2); hp.position.set(p.x, 0.81, p.z); hp.rotation.y = L.gateAngle;
    hp.name = 'gatePortal'; scene.add(hp);

    // Wounds in the gate stone: molten cracks spread from the arch as breaches accumulate and flare on each one.
    const archY = 1.15, archTop = archY + gateW / 2 + 0.2, CW = gateW + 0.6, CH = archTop, cy = archTop / 2;
    const cracks = new THREE.Mesh(new THREE.PlaneGeometry(CW, CH), new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uWound: gateWound },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2,
      vertexShader: 'varying vec2 vP; void main(){ vP = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform vec2 uWound; varying vec2 vP;
        float h2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        float vn(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
          return mix(mix(h2(i), h2(i + vec2(1, 0)), f.x), mix(h2(i + vec2(0, 1)), h2(i + vec2(1, 1)), f.x), f.y); }
        void main() {
          vec2 w = vec2(vP.x, vP.y + ${cy.toFixed(2)});
          // the opening itself is the portal's
          if (abs(w.x) < ${(gateW / 2).toFixed(2)} && (w.y < ${archY.toFixed(2)} || length(vec2(w.x, w.y - ${archY.toFixed(2)})) < ${(gateW / 2).toFixed(2)})) discard;
          float wound = uWound.x;
          if (wound < 0.01) discard;
          // distance from the opening's edge: cracks start at the arch and creep outward as the wound grows
          float edgeD = max(abs(w.x) - ${(gateW / 2).toFixed(2)}, length(vec2(w.x, max(0.0, w.y - ${archY.toFixed(2)}))) - ${(gateW / 2).toFixed(2)});
          float spread = smoothstep(wound * 0.9 + 0.05, wound * 0.9 - 0.15, edgeD);
          // warped noise ridges read as branching fractures rather than cells
          vec2 ww = w * 7.0 + vec2(vn(w * 3.0) * 1.6, vn(w * 3.0 + 9.1) * 1.6);
          float r1 = abs(vn(ww) - 0.5), r2 = abs(vn(ww * 2.3 + 3.7) - 0.5);
          float crack = (smoothstep(0.022, 0.0, r1) + smoothstep(0.014, 0.0, r2) * 0.45 * step(0.35, wound)) * spread;
          float surge = exp(-max(0.0, uTime - uWound.y) * 2.0);
          float beat = 0.75 + 0.25 * sin(uTime * (2.0 + wound * 4.0) + w.y * 3.0);
          gl_FragColor = vec4(vec3(1.4, 0.35, 0.06) * crack * (0.6 + wound * 0.9) * (beat + surge * 1.5), 1.0);
        }`,
    }));
    const cp = gatePoint(0, 0.31); cracks.position.set(cp.x, cy, cp.z); cracks.rotation.y = L.gateAngle;
    cracks.name = 'gateCracks'; cracks.renderOrder = 2; scene.add(cracks);
  }
  const bar = new THREE.CylinderGeometry(0.022, 0.022, 1.6, 6), spike = new THREE.ConeGeometry(0.04, 0.14, 4);
  for (let i = 0; i < 6; i++) {
    const u = -gateW / 2 + 0.1 + i * (gateW - 0.2) / 5;
    gatePart(bar, M.iron, u, 1.35, 0.05, 0, [1, 0.35, 1]);
    gatePart(spike, M.iron, u, 1.12, 0.05, Math.PI);
  }
  gatePart(new THREE.BoxGeometry(gateW, 0.045, 0.045), M.iron, 0, 1.35, 0.05);
  const gateFire = gatePoint(0, 0.7);
  const flames: WorldInfo['flames'] = [{ pos: new THREE.Vector3(gateFire.x, 0.45, gateFire.z), color: 0xff6a28, rate: 8, radius: 0.5, fire: 6, fireSize: 0.32 }];
  fire(0xff4c1c, 10, 5, gateFire.x, 0.55, gateFire.z, 1);
  glows.add(0xff5a20, gateFire.x, 0.8, gateFire.z, { size: 1.6, opacity: 0.45, flicker: 0.15 });
  gatePart(new THREE.BoxGeometry(gateW + 0.35, 0.08, 1.45), M.basalt, 0, 0.035, 0.2);

  /* near curb with heat strip */
  const curbZ = L.curbZ;
  batch.add(new THREE.BoxGeometry(RL, 0.12, 0.22), M.wall, L.roadCenterX, 0.06, curbZ);
  {
    const strip = new THREE.BoxGeometry(0.38, 0.02, 0.06);
    for (let i = 0; i < 18; i++) {
      const x = L.spawnX + 0.25 + (i / 17) * (gateX - L.spawnX - 0.25);
      const heat = Math.pow(Math.max(0, (x - (gateX - 3.6)) / 3.6), 2.2), col = new THREE.Color(0x1c0d06).lerp(new THREE.Color(0xff3010), Math.min(1, heat));
      batch.add(strip, stripMat(col.multiplyScalar(1 + heat * 1.5)), x, 0.125, curbZ - 0.06, { cast: false });
      if (heat > 0.2) glows.add(0xff4a18, x, 0.15, curbZ - 0.06, { size: 0.55, opacity: 0.45 * heat, flicker: 0.12 });
    }
  }
  /* braziers on curb */
  [-3.4, -0.6].forEach((x) => {
    batch.add(new THREE.CylinderGeometry(0.13, 0.08, 0.22, 10), M.iron, x, 0.23, curbZ);
    // no real light: an additive pool on the stones reads the same at a fraction of the cost
    ctx.floorGlows.add(0xff8a3a, x, 0.02, curbZ - 0.35, { size: 1.6, opacity: 0.35, flicker: 0.25 });
    ctx.floorGlows.add(0xff8a3a, x, 0.13, curbZ, { size: 0.9, opacity: 0.5, flicker: 0.25 });
    glows.add(0xff7a2a, x, 0.44, curbZ, { size: 0.8, opacity: 1.1, flicker: 0.25 });
    glows.add(0xffd090, x, 0.38, curbZ, { size: 0.3, opacity: 1.6, flicker: 0.2 });
    flames.push({ pos: new THREE.Vector3(x, 0.4, curbZ), color: 0xff8a3a, rate: 3.2, radius: 0.07, fire: 7, fireSize: 0.16 });
  });

  /* Banners and torches live on the perimeter, leaving both altars in one open courtyard. */
  // Banners sway on the breeze: one separate draw whose vertex shader swings each cloth from its pole,
  // the hem moving most, every banner on its own phase.
  const bannerParts: THREE.BufferGeometry[] = [];
  [-4.7, -6.4, -8.1].forEach((bz, i) => {
    const bx = L.cornerX - 0.3, rot: [number, number, number] = [0, -Math.PI / 2, 0];
    const pg = new THREE.PlaneGeometry(0.56, 1.05, 6, 10), pp = pg.attributes.position;
    const sway = new Float32Array(pp.count * 2);
    for (let k = 0; k < pp.count; k++) {
      const xx = pp.getX(k), yy = pp.getY(k);
      pp.setZ(k, Math.sin((yy + xx * 0.6) * 5 + i) * 0.025 + (yy < -0.3 ? (hash(k, i, 4) - 0.5) * 0.03 : 0));
      if (yy < -0.45) pp.setY(k, yy + (Math.abs(xx) < 0.08 ? 0.16 : 0) + (hash(k, i, 6) - 0.5) * 0.05);
      sway[k * 2] = THREE.MathUtils.clamp((0.525 - yy) / 1.05, 0, 1); sway[k * 2 + 1] = i * 2.1;
    }
    pg.setAttribute('aSway', new THREE.BufferAttribute(sway, 2));
    pg.applyMatrix4(new THREE.Matrix4().makeRotationY(rot[1]).setPosition(bx, 1.0, bz));
    pg.computeVertexNormals();
    bannerParts.push(pg);
    batch.add(new THREE.CylinderGeometry(0.018, 0.018, 0.7, 8).rotateZ(Math.PI / 2), M.iron, bx - 0.02, 1.53, bz, { rot });
    batch.add(new THREE.BoxGeometry(0.04, 0.3, 0.02), M.trim, bx - 0.015, 1.15, bz, { rot });
  });
  {
    const cloth = M.cloth.clone();
    cloth.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = fxUniforms.uTime;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime; attribute vec2 aSway;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          float hem = aSway.x * aSway.x;
          float gust = 0.6 + 0.4 * sin(uTime * 0.37 + aSway.y);
          transformed.x += (sin(uTime * 1.8 + aSway.y + position.y * 3.2) * 0.07 + 0.03) * hem * gust;
          transformed.z += sin(uTime * 1.3 + aSway.y * 1.7 + position.y * 2.0) * 0.03 * hem * gust;`);
    };
    cloth.customProgramCacheKey = () => 'banner-sway';
    const banners = new THREE.Mesh(mergeGeometries(bannerParts)!, cloth);
    bannerParts.forEach((g) => g.dispose());
    banners.name = 'banners'; banners.receiveShadow = false; banners.matrixAutoUpdate = false; banners.frustumCulled = false;
    scene.add(banners);
  }
  [-5.55, -8.95].forEach((tz) => {
    const tx = L.cornerX - 0.7;
    batch.add(new THREE.BoxGeometry(0.14, 0.22, 0.06), M.iron, tx + 0.08, 1.05, tz);
    batch.add(new THREE.CylinderGeometry(0.06, 0.035, 0.14, 10), M.iron, tx, 1.2, tz);
    glows.add(0xff8a3a, tx - 0.02, 1.34, tz, { size: 0.6, opacity: 1.2, flicker: 0.25 });
    glows.add(0xffe0a0, tx - 0.02, 1.3, tz, { size: 0.22, opacity: 1.8, flicker: 0.2 });
    flames.push({ pos: new THREE.Vector3(tx - 0.02, 1.33, tz), color: 0xffa04a, rate: 2.2, radius: 0.04, fire: 6, fireSize: 0.1 });
    ctx.floorGlows.add(0xff8a3a, tx, 0.02, tz, { size: 1.5, opacity: 0.3, flicker: 0.25 });
    glows.add(0xff7a2a, tx + 0.25, 1.1, tz, { size: 1.4, opacity: 0.18, flicker: 0.25 });
  });
  {
    const rock = new THREE.DodecahedronGeometry(1, 0);
    for (let i = 0; i < 70; i++) {
      const side = hash(i, 1, 41) > 0.5, x = -4.3 + hash(i, 2, 41) * 8.2;
      const z = side ? CZ - RD / 2 + 0.05 + hash(i, 3, 41) * 0.25 : CZ + RD / 2 - 0.05 - hash(i, 4, 41) * 0.3;
      const r0 = 0.025 + hash(i, 5, 41) ** 2 * 0.07;
      batch.add(rock, M.base, x, r0 * 0.6, z, { scale: r0, rot: [hash(i, 6, 41) * 3, hash(i, 7, 41) * 3, 0] });
    }
  }

  /* Ruins beyond the field's right edge: broken columns, rubble and two distant braziers (all batched). */
  {
    const rock = new THREE.DodecahedronGeometry(1, 0);
    // placed relative to the gate so they follow it when the layout shifts
    const rx = L.gateX - 4.6;
    ([[6.6, -5.2, 1.5], [8.3, -2.4, 0.7], [7.4, 1.8, 1.15], [6.3, 5.6, 0.55], [9.2, 4.0, 1.4]] as const).forEach(([x0, z, h], i) => {
      const x = x0 + rx;
      const col = new THREE.CylinderGeometry(0.24, 0.29, h, 10, 2), cp = col.attributes.position;
      for (let k = 0; k < cp.count; k++) if (cp.getY(k) > h / 2 - 0.01) cp.setY(k, cp.getY(k) - hash(k, i, 71) * 0.28);
      col.computeVertexNormals();
      batch.add(col, M.wall, x, h / 2, z, { rot: [hash(i, 2, 71) * 0.06, hash(i, 3, 71) * 3, 0] });
      batch.add(new THREE.CylinderGeometry(0.36, 0.4, 0.14, 10), M.base, x, 0.07, z);
      for (let r = 0; r < 7; r++) {
        const a = hash(r, i, 72) * 6.28, d = 0.35 + hash(r, i, 73) * 0.7, s0 = 0.05 + hash(r, i, 74) ** 2 * 0.16;
        batch.add(rock, r % 3 ? M.base : M.wall, x + Math.cos(a) * d, s0 * 0.5, z + Math.sin(a) * d, { scale: s0, rot: [hash(r, i, 75) * 3, hash(r, i, 76) * 3, 0] });
      }
    });
    // solo frames closer, so the flank ruins above fall outside it: a compact cluster fills the visible
    // empty floor to the right of the board instead
    const near: readonly (readonly [number, number, number])[] = coop ? [] : [[5.3, 1.6, 1.05], [4.7, 4.9, 0.6], [5.9, 6.6, 1.3]];
    near.forEach(([x, z, h], j) => {
      const i = 10 + j;
      const col = new THREE.CylinderGeometry(0.22, 0.27, h, 10, 2), cp = col.attributes.position;
      for (let k = 0; k < cp.count; k++) if (cp.getY(k) > h / 2 - 0.01) cp.setY(k, cp.getY(k) - hash(k, i, 71) * 0.26);
      col.computeVertexNormals();
      batch.add(col, M.wall, x, h / 2, z, { rot: [hash(i, 2, 71) * 0.08, hash(i, 3, 71) * 3, 0] });
      batch.add(new THREE.CylinderGeometry(0.34, 0.38, 0.13, 10), M.base, x, 0.065, z);
      for (let r = 0; r < 6; r++) {
        const a = hash(r, i, 72) * 6.28, d = 0.35 + hash(r, i, 73) * 0.6, s0 = 0.05 + hash(r, i, 74) ** 2 * 0.14;
        batch.add(rock, r % 3 ? M.base : M.wall, x + Math.cos(a) * d, s0 * 0.5, z + Math.sin(a) * d, { scale: s0, rot: [hash(r, i, 75) * 3, hash(r, i, 76) * 3, 0] });
      }
    });
    ([[7.7, -0.4], [6.9, 3.7], ...(coop ? [] : [[4.6, 3.1]])] as const).forEach(([x0, z], bi) => {
      const x = bi < 2 ? x0 + rx : x0;
      batch.add(new THREE.CylinderGeometry(0.13, 0.08, 0.22, 10), M.iron, x, 0.23, z);
      batch.add(new THREE.CylinderGeometry(0.03, 0.05, 0.12, 6), M.iron, x, 0.06, z);
      glows.add(0xff7a2a, x, 0.44, z, { size: 0.8, opacity: 0.9, flicker: 0.25 });
      ctx.floorGlows.add(0xff8a3a, x, 0.02, z, { size: 1.6, opacity: 0.3, flicker: 0.25 });
      flames.push({ pos: new THREE.Vector3(x, 0.4, z), color: 0xff8a3a, rate: 2.5, radius: 0.07, fire: 6, fireSize: 0.15 });
    });
  }

  /* partner altar on a plinth (co-op only: solo has no unplayable board to show) */
  const partnerAltar = coop ? buildAltar(ctx, 0, L.partnerZ, BOARD_PARTNER, 0.98, 0.7, 0.18, 0.16, {
    plinth: 0.45, y0: 0.5, mat: M.basalt, inlayMat: M.bronze, bh: 0.2, th: 0.14, pad: 0.28, grooves: true,
  }) : null;
  if (coop) {
    blob(ctx, 0, 0.006, L.partnerZ, 7, 5, 0.7);
    // ally marker: a cool turquoise outline around the partner's altar with a slow light running along it,
    // and a soft pool beneath — distinct from the warm, fiery palette of the player and the horde
    const Wd = 4 * 0.98 + 3 * 0.18 + 2 * 0.28, Dd = 4 * 0.7 + 3 * 0.16 + 2 * 0.28;
    const outline = new THREE.Mesh(ext(ring(octShape(Wd + 0.62, Dd + 0.62, 0.68), octShape(Wd + 0.5, Dd + 0.5, 0.63)), 0.01),
      new THREE.ShaderMaterial({
        uniforms: { uTime: fxUniforms.uTime }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
        vertexShader: 'varying vec3 vP; void main(){ vP = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
        fragmentShader: `uniform float uTime; varying vec3 vP;
          void main() {
            float a = atan(vP.z, vP.x);
            float run = pow(max(0.0, cos(a - uTime * 0.6)), 24.0) + pow(max(0.0, cos(a - uTime * 0.6 + 3.1416)), 24.0);
            float breathe = 0.75 + 0.25 * sin(uTime * 1.3);
            gl_FragColor = vec4(vec3(0.2, 0.8, 0.9) * (0.55 * breathe + run * 2.2), 1.0);
          }`,
      }));
    outline.position.set(0, 0.5 + 0.2 + 0.08 + 0.14 + 0.07, L.partnerZ); outline.renderOrder = 3; outline.name = 'allyOutline';
    scene.add(outline);
    ctx.floorGlows.add(0x3fc6d8, 0, 0.02, L.partnerZ, { size: 6.5, opacity: 0.16, flicker: 0.05 });
  }

  /* my altar */
  // the same near altar (4×4) in both modes, as the simulation has it
  const cw = 1.18, cd = 0.84, gx = 0.2, gz = 0.2;
  const myAltar = buildAltar(ctx, 0, 2.55, myGrid, cw, cd, gx, gz, { pad: 0.32, bh: 0.28, th: 0.22, interactive: true });
  {
    // A rune circle burned into the floor around the player's altar: it smoulders and turns slowly,
    // and every merge or summon sends a wave of light around it (see runeFlash).
    const rz = 2.55, R = 4.1;
    const rune = new THREE.Mesh(new THREE.PlaneGeometry(R * 2, R * 2).rotateX(-Math.PI / 2), new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uFlash: runeFlash, uOverload: runeOverload },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: 'varying vec2 vP; void main(){ vP = position.xz; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform float uTime, uOverload; uniform vec2 uFlash; varying vec2 vP;
        float h1(float n) { return fract(sin(n * 91.7) * 43758.5453); }
        float line(float d, float w) { return 1.0 - smoothstep(w, w + fwidth(d) * 1.5, abs(d)); }
        void main() {
          float r = length(vP), a = atan(vP.y, vP.x);
          if (r > ${(R - 0.02).toFixed(2)} || r < 3.2) discard;
          float rings = line(r - 3.45, 0.012) + line(r - 3.98, 0.016) + line(r - 3.9, 0.006) * 0.6;
          // the glyph band turns slowly; each of 56 cells holds a short bar pattern of its own
          float u = (a + uTime * 0.012) / 6.2832 * 56.0, cell = floor(u), f = fract(u);
          float hv = h1(cell), hv2 = h1(cell + 17.0);
          float v = (r - 3.53) / 0.3;
          float glyph = step(0.0, v) * step(v, 1.0) * (
              step(abs(f - 0.5), 0.07) * step(hv, 0.8)
            + step(abs(v - 0.3 - hv * 0.4), 0.06) * step(abs(f - 0.5), 0.3) * step(0.35, hv2)
            + step(abs(f - 0.2 - hv2 * 0.6), 0.05) * step(v, 0.55) * step(0.5, hv));
          float shape = rings + glyph * 0.8;
          // smoulder: slow breathing, brighter where the glyphs are fresh
          float base = 0.06 + 0.03 * sin(uTime * 0.8 + cell * 0.7);
          // flash: a wave racing around the circle from the front, fading over a second
          float age = uTime - uFlash.x;
          float sweep = smoothstep(0.0, 0.08, 1.0 - abs(fract((a / 6.2832 + 0.25) - age * 0.9 + 0.5) - 0.5) * 2.0 - 0.6);
          float flash = uFlash.y * exp(-age * 1.8) * (0.35 + sweep * 1.4) * step(0.0, age);
          // overload: the circle burns gold, with charge racing around it
          float race = pow(max(0.0, sin((a / 6.2832 * 3.0 - uTime * 1.8) * 6.2832)), 4.0);
          float over = uOverload * (0.45 + 0.9 * race);
          vec3 col = mix(vec3(1.0, 0.5, 0.16), vec3(1.25, 0.95, 0.45), uOverload) * shape * (base + flash + over);
          float edgeFade = smoothstep(${(R - 0.02).toFixed(2)}, ${(R - 0.12).toFixed(2)}, r)
            // the far arc fades out before it would climb the road curb
            * smoothstep(${(L.curbZ + 0.25 - rz).toFixed(2)}, ${(L.curbZ + 0.9 - rz).toFixed(2)}, vP.y);
          gl_FragColor = vec4(col * edgeFade, 1.0);
        }`,
    }));
    rune.position.set(0, 0.012, rz); rune.renderOrder = 1; rune.name = 'altarRunes';
    scene.add(rune);
  }

  /* summon seal */
  const SZ = 6.05, SS = L.sealScale, SX = L.sealX;
  const sealAdd = (geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, opts: Parameters<StaticBatch['add']>[5] = {}) => {
    batch.add(geo, mat, SX + x * SS, y * SS, SZ + (z - SZ) * SS, { ...opts, scale: SS });
  };
  sealAdd(new THREE.CylinderGeometry(1.55, 1.62, 0.14, 80), M.base, 0, 0.07, SZ);
  sealAdd(new THREE.CylinderGeometry(1.4, 1.44, 0.14, 80), M.basalt, 0, 0.21, SZ);
  sealAdd(new THREE.TorusGeometry(1.12, 0.085, 8, 120).rotateX(Math.PI / 2), M.groove, 0, 0.28, SZ, { cast: false });
  const arcMat = new THREE.ShaderMaterial({
    uniforms: { uFill: { value: 0.72 }, uColor: { value: new THREE.Color(0xff8a36).multiplyScalar(1.5) }, uFlash: { value: 0 } },
    vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float uFill, uFlash; uniform vec3 uColor; varying vec2 vUv;
      void main() {
        if (vUv.x > uFill) discard;
        float head = smoothstep(uFill - 0.06, uFill, vUv.x);
        vec3 c = uColor * (1.0 + head * 1.8 + uFlash * 2.5) + vec3(1.0, 0.85, 0.6) * head * 1.5;
        gl_FragColor = vec4(c, 1.0);
      }`,
  });
  const spArcMesh = new THREE.Mesh(new THREE.TorusGeometry(1.12, 0.06, 10, 140, Math.PI * 2).scale(SS, SS, SS), arcMat);
  spArcMesh.rotation.x = -Math.PI / 2; spArcMesh.rotation.z = Math.PI / 2; spArcMesh.position.set(SX, 0.345 * SS, SZ); scene.add(spArcMesh);
  const arcGlows: number[] = [];
  for (let i = 0; i < 40; i++) {
    const a = Math.PI / 2 + (i / 40) * Math.PI * 2;
    arcGlows.push(glows.add(0xff7a2a, SX + Math.cos(a) * 1.12 * SS, 0.37 * SS, SZ - Math.sin(a) * 1.12 * SS, { size: 0.5 * SS, opacity: 0.3 }));
  }
  sealAdd(new THREE.TorusGeometry(1.4, 0.016, 6, 120).rotateX(Math.PI / 2), M.bronze, 0, 0.285, SZ, { cast: false });
  sealAdd(new THREE.TorusGeometry(0.86, 0.014, 6, 100).rotateX(Math.PI / 2), M.bronze, 0, 0.285, SZ, { cast: false });
  {
    const tickA = new THREE.BoxGeometry(0.03, 0.02, 0.1), tickB = new THREE.BoxGeometry(0.03, 0.02, 0.18);
    for (let i = 0; i < 32; i++) { const a = (i / 32) * Math.PI * 2; sealAdd(i % 4 ? tickA : tickB, M.groove, Math.cos(a) * 1.3, 0.285, SZ + Math.sin(a) * 1.3, { cast: false, rot: [0, -a + Math.PI / 2, 0] }); }
  }
  sealAdd(new THREE.CylinderGeometry(0.8, 0.84, 0.1, 64), M.basalt, 0, 0.33, SZ);
  const sealStar = new THREE.Mesh(ext(star4(0.74, 0.16), 0.08, 0.05).scale(SS, SS, SS), M.bronze); sealStar.position.set(SX, 0.38 * SS, SZ); sealStar.castShadow = true; scene.add(sealStar);
  sealAdd(ext(star4(0.44, 0.11), 0.06, 0.04), M.trim, 0, 0.38, SZ, { rot: [0, Math.PI / 4, 0] });
  const sealCore = new THREE.Mesh(new THREE.SphereGeometry(0.14 * SS, 24, 16), new THREE.MeshPhysicalMaterial({ name: 'summonCore', color: 0xff9040, emissive: 0xff7020, emissiveIntensity: 1.6, roughness: 0.1, clearcoat: 1 }));
  sealCore.position.set(SX, 0.62 * SS, SZ); scene.add(sealCore);
  glows.add(0xff8030, SX, 0.62 * SS, SZ, { size: 1.3 * SS, opacity: 0.8, flicker: 0.1 });
  // the seal's warm light is shader-only (slot 2) on the stone; its metal star glows by itself instead,
  // since metal only takes specular light, which the shader-only lights do not model
  fire(0xff7a30, 4.5, 2.6, SX, 0.9 * SS, SZ, 3);
  ctx.floorGlows.add(0xff7a30, SX, 0.02, SZ, { size: 3.2 * SS, opacity: 0.25, flicker: 0.12 });
  const starMat = (M.bronze as THREE.MeshStandardMaterial).clone();
  starMat.name = 'sealStar'; starMat.emissive.set(0xff8a3c); starMat.emissiveIntensity = 0.32;
  sealStar.material = starMat;
  blob(ctx, SX, 0.006, SZ, 4 * SS, 4 * SS, 0.8);
  // the one skill's socket (a battle has one skill), where the medallion stands, a size larger
  {
    const x = -2.275, z = SZ, k = 1.25;
    batch.add(new THREE.CylinderGeometry(0.48 * k, 0.53 * k, 0.13, 8), M.base, x, 0.065, z);
    batch.add(new THREE.CylinderGeometry(0.43 * k, 0.46 * k, 0.09, 8), M.basalt, x, 0.17, z);
    batch.add(new THREE.TorusGeometry(0.4 * k, 0.016, 4, 8).rotateX(Math.PI / 2), M.bronze, x, 0.222, z, { cast: false });
  }
  /* A single substantial artifact beside each board, clear of the road and summon controls. */
  const totemSockets: WorldInfo['totemSockets'] = [{ owner: 'player', x: -3.87, z: 2.5, y: 0.32 }];
  if (coop) totemSockets.push({ owner: 'ally', x: -3.47, z: L.partnerZ, y: 0.48 });
  for (const socket of totemSockets) {
    const { x, z, y } = socket;
    batch.add(new THREE.CylinderGeometry(0.55, 0.63, y - 0.12, 8), M.base, x, (y - 0.12) / 2, z);
    batch.add(new THREE.CylinderGeometry(0.48, 0.55, 0.12, 8), M.basalt, x, y - 0.06, z);
    batch.add(new THREE.TorusGeometry(0.475, 0.018, 4, 32).rotateX(-Math.PI / 2), M.bronze, x, y + 0.009, z, { cast: false });
    for (let k = 0; k < 8; k++) {
      const a = k * Math.PI / 4;
      batch.add(new THREE.BoxGeometry(0.06, 0.018, 0.09), M.trim, x + Math.sin(a) * 0.4, y + 0.016, z + Math.cos(a) * 0.4, { rot: [0, a, 0], cast: false });
    }
    blob(ctx, x, 0.006, z, 1.7, 1.7, 0.7);
  }

  /* gate light shaft on road + haze */
  {
    const sh = new THREE.Mesh(new THREE.PlaneGeometry(1.5, RD + 0.4).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ map: tex.shaft, color: new THREE.Color(0xff6a2a).multiplyScalar(0.7), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    sh.position.set(gateFire.x, 0.03, gateFire.z); sh.rotation.y = L.gateAngle; sh.renderOrder = 2; scene.add(sh);
  }
  const smoke: WorldInfo['smoke'] = [];
  const camPos = new THREE.Vector3(0, 15.4, 13.4);
  {
    // Haze sheets: the five dark ones share one draw — tint, opacity, tiling and drift ride per vertex,
    // sorted far → near; the additive glow over the gate fire keeps its own material.
    const sheets = ([[0, 0.55, CZ + 0.3, 9, 1.6, 0.2, 0x3a2a20], [1.5, 1.0, CZ - 0.4, 6, 1.4, 0.16, 0x5a2a14], [-2, 0.9, CZ - 0.2, 6, 1.2, 0.14, 0x2c2420],
      [gateFire.x, 0.9, gateFire.z, 2.0, 1.6, 0.18, 0xff5a20], [0, 0.5, -7.4, 14, 2, 0.18, 0x1c1916], [0, 0.4, 8.6, 14, 2.2, 0.2, 0x120f0d]] as const)
      .map(([x, y, z, w, h, op, c], i) => ({ x, y, z, w, h, op, c, sp: 0.006 + i * 0.002, add: i === 3 }));
    const o = new THREE.Object3D(), geos: THREE.BufferGeometry[] = [], col = new THREE.Color();
    for (const q of sheets.filter(q => !q.add).sort((a, b) => camPos.distanceToSquared(new THREE.Vector3(b.x, b.y, b.z)) - camPos.distanceToSquared(new THREE.Vector3(a.x, a.y, a.z)))) {
      o.position.set(q.x, q.y, q.z); o.lookAt(camPos); o.updateMatrix();
      const g = new THREE.PlaneGeometry(q.w, q.h).applyMatrix4(o.matrix), n = g.attributes.position.count;
      col.set(q.c);
      g.setAttribute('aTint', new THREE.Float32BufferAttribute(Array.from({ length: n }, () => [col.r, col.g, col.b, q.op]).flat(), 4));
      g.setAttribute('aDrift', new THREE.Float32BufferAttribute(Array.from({ length: n }, () => [q.w / 3, q.sp]).flat(), 2));
      geos.push(g);
    }
    const hazeTex = tex.smoke.clone(); hazeTex.wrapS = hazeTex.wrapT = THREE.RepeatWrapping; hazeTex.needsUpdate = true;
    const haze = new THREE.Mesh(mergeGeometries(geos)!, new THREE.ShaderMaterial({
      name: 'haze', fog: true, transparent: true, depthWrite: false,
      uniforms: { ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), map: { value: hazeTex }, uTime: fxUniforms.uTime },
      vertexShader: /* glsl */ `
        #include <common>
        #include <fog_pars_vertex>
        attribute vec4 aTint; attribute vec2 aDrift; uniform float uTime; varying vec4 vTint; varying vec2 vUv;
        void main() {
          vTint = aTint; vUv = vec2(uv.x * aDrift.x + uTime * aDrift.y, uv.y);
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          #include <fog_vertex>
        }`,
      fragmentShader: /* glsl */ `
        #include <common>
        #include <fog_pars_fragment>
        uniform sampler2D map; varying vec4 vTint; varying vec2 vUv;
        void main() {
          vec4 t = texture2D(map, vUv);
          gl_FragColor = vec4(vTint.rgb * t.rgb, vTint.a * t.a);
          #include <fog_fragment>
        }`,
    }));
    geos.forEach(g => g.dispose());
    haze.name = 'haze'; haze.renderOrder = 6; haze.matrixAutoUpdate = false; scene.add(haze);
    for (const q of sheets.filter(q => q.add)) {
      const t = tex.smoke.clone(); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(q.w / 3, 1); t.needsUpdate = true;
      const m = new THREE.Mesh(new THREE.PlaneGeometry(q.w, q.h), new THREE.MeshBasicMaterial({ map: t, color: new THREE.Color(q.c).multiplyScalar(1.5), transparent: true, opacity: q.op, depthWrite: false, blending: THREE.AdditiveBlending }));
      m.position.set(q.x, q.y, q.z); m.lookAt(camPos); m.renderOrder = 6; scene.add(m);
      smoke.push({ t, sp: q.sp });
    }
  }
  buildSpawnFog(scene, ctx.fogLite);
  const motes = ctx.dust ?? 360, dustG = new THREE.BufferGeometry(), dp: number[] = [], dustSeed = new Float32Array(motes);
  for (let i = 0; i < motes; i++) { dp.push(gateX - 2.6 + hash(i, 1, 51) * 3.4, 0.1 + hash(i, 2, 51) * 1.6, CZ - 1 + hash(i, 3, 51) * 2.2); dustSeed[i] = hash(i, 4, 51) * 6; }
  dustG.setAttribute('position', new THREE.Float32BufferAttribute(dp, 3));
  dustG.setAttribute('aSeed', new THREE.BufferAttribute(dustSeed, 1));
  const dustMat = new THREE.PointsMaterial({ color: new THREE.Color(0xffa060).multiplyScalar(1.4), size: 0.035, map: tex.glow, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false });
  dustMat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = fxUniforms.uTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime; attribute float aSeed;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        transformed.y = 0.1 + mod(position.y - 0.1 + uTime * 0.075, 1.7);
        transformed.x += sin(uTime * 0.7 + aSeed) * 0.064;`);
  };
  dustMat.customProgramCacheKey = () => 'altar-dust';
  const dust = new THREE.Points(dustG, dustMat);
  dust.frustumCulled = false; scene.add(dust);

  // Ash over the whole courtyard: grey flakes settle slowly on the breeze and flutter, one in ten still
  // smoulders. Positions wrap inside a box in the vertex shader, so there is no CPU work at all.
  {
    const n = Math.round((ctx.dust ?? 360) * 0.6), ap = new Float32Array(n * 3), aa = new Float32Array(n);
    for (let i = 0; i < n; i++) { ap.set([hash(i, 1, 61) * 18, hash(i, 2, 61) * 4, hash(i, 3, 61) * 16], i * 3); aa[i] = hash(i, 4, 61); }
    const ag = new THREE.BufferGeometry();
    ag.setAttribute('position', new THREE.BufferAttribute(ap, 3)); ag.setAttribute('aSeed', new THREE.BufferAttribute(aa, 1));
    const ash = new THREE.Points(ag, new THREE.ShaderMaterial({
      uniforms: { uTime: fxUniforms.uTime, uMap: { value: tex.glow }, uScale: { value: 1 } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        uniform float uTime, uScale; attribute float aSeed; varying float vEmber, vFade;
        void main() {
          vec3 p = position;
          // drift with the breeze, settle slowly, flutter; wrap inside an 18 x 4 x 16 m box over the courtyard
          p.x = mod(p.x + uTime * (0.14 + aSeed * 0.08), 18.0) - 8.0;
          p.z = mod(p.z - uTime * 0.05, 16.0) - 6.5;
          p.y = mod(p.y - uTime * (0.05 + aSeed * 0.05), 4.0);
          p.x += sin(uTime * (0.9 + aSeed) + aSeed * 40.0) * 0.12;
          p.z += cos(uTime * (0.7 + aSeed) + aSeed * 23.0) * 0.1;
          vEmber = step(0.9, aSeed);
          // fade in at the top and out near the ground so wrapping never pops
          vFade = smoothstep(0.0, 0.4, p.y) * smoothstep(4.0, 3.3, p.y);
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          // world-sized: flakes ~4 cm, embers ~3 cm, scaled to the drawing buffer like PointsMaterial
          gl_PointSize = (vEmber > 0.5 ? 0.03 : 0.04) * (0.6 + aSeed * 0.8) * projectionMatrix[1][1] * uScale / -mv.z;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform sampler2D uMap; varying float vEmber, vFade;
        void main() {
          float a = texture2D(uMap, gl_PointCoord).a;
          vec3 col = vEmber > 0.5 ? vec3(1.6, 0.6, 0.18) : vec3(0.34, 0.36, 0.42);
          gl_FragColor = vec4(col * a * vFade, 1.0);
        }`,
    }));
    const buf = new THREE.Vector2();
    ash.onBeforeRender = (r) => { (ash.material as THREE.ShaderMaterial).uniforms.uScale.value = r.getDrawingBufferSize(buf).y * 0.5; };
    ash.name = 'ash'; ash.frustumCulled = false; scene.add(ash);
  }

  return { myAltar, partnerAltar, sealPos: new THREE.Vector3(SX, 0.62 * SS, SZ), smoke, dust, key, spArc: { mesh: spArcMesh, mat: arcMat, glows: arcGlows }, sealCore, sealStar, totemSockets, flames };
}

/**
 * The floor and road fill most of the screen: they read the shadow map once (still 2×2 hardware-filtered)
 * instead of three.js's five-tap Vogel disk. Small objects keep the soft five-tap shadows.
 */
const CHEAP_SHADOWS = THREE.ShaderChunk.shadowmap_pars_fragment.replace(
  /shadow = \(\s*texture\( shadowMap, vec3\( shadowCoord\.xy \+ vogelDiskSample\( 0[\s\S]*?\) \* 0\.2;/,
  'shadow = texture( shadowMap, vec3( shadowCoord.xy, shadowCoord.z ) );');
function cheapShadows(fragment: string) { return fragment.replace('#include <shadowmap_pars_fragment>', CHEAP_SHADOWS); }

const stripMats = new Map<string, THREE.MeshBasicMaterial>();
function stripMat(c: THREE.Color) {
  // quantise so strips share a few materials (and therefore merge into few draws)
  const q = new THREE.Color(Math.round(c.r * 8) / 8, Math.round(c.g * 8) / 8, Math.round(c.b * 8) / 8), k = q.getHexString() + q.r;
  let m = stripMats.get(k);
  if (!m) { m = new THREE.MeshBasicMaterial({ color: q }); stripMats.set(k, m); }
  return m;
}
